import React, { useEffect, useMemo, useState } from 'react';
import { ZenTabPane, ZenTabs } from '../../../../components/tabs';
import { defaultColors } from '../../../../constants/color';
import { ColorSetting, useEditor } from '@mnz/builder';

import classNames from 'classnames';
import ColorPicker from '../../../../components/color-picker/ColorPicker';
import Dialog, { DialogProps } from '../../../../components/dialog/Dialog';
import { UploadImageInput } from '../../../../components/form';
import { MenuItem, Select } from '@mui/material';

interface ChooseColorModalProps extends DialogProps {
    type: string;
}

type ColorTabKey = 'select' | 'custom' | 'image';

const tabList = (component: string) => {
    const result = [
        {
            key: 'select',
            tab: 'Select',
        },
        {
            key: 'custom',
            tab: 'Custom',
        },
    ];
    if (component === 'background') {
        result.push({
            key: 'image',
            tab: 'Image',
        });
    }
    return result;
};

const getTab = (setting?: ColorSetting): ColorTabKey =>
    setting?.backgroundImage
        ? 'image'
        : defaultColors.some((colorList) => colorList.some((color) => color === setting?.color))
        ? 'select'
        : 'custom';

const ChooseColorModal: React.FC<ChooseColorModalProps> = ({ type, ...props }) => {
    const {
        designSettings,
        actions: { setColors },
    } = useEditor((_, query) => ({
        designSettings: query.getDesignSettings(),
    }));
    const [tab, setTab] = useState<ColorTabKey>('select');
    const background = designSettings?.colors?.background;
    const textColor = designSettings?.colors?.text;
    const buttonColor = designSettings?.colors?.button;
    const buttonTextColor = designSettings?.colors?.buttonText;
    const socialButtonBackground = designSettings?.colors?.socialButton;

    const handleChangeColor = (type: string, color: string) => {
        setColors(type, { color });
    };

    const handleChangeBackground = (backgroundImage?: string) => {
        setColors(type, { backgroundImage });
    };

    const handleChangeBackgroundStyle = (backgroundStyle: string) => {
        setColors(type, { backgroundStyle });
    };

    const currentColor = useMemo(() => {
        if (type === 'background') {
            return background;
        }
        if (type === 'text') {
            return textColor;
        }
        if (type === 'button') {
            return buttonColor;
        }
        if (type === 'buttonText') {
            return buttonTextColor;
        }
        if (type === 'socialButton') {
            return socialButtonBackground;
        }
    }, [background, buttonColor, buttonTextColor, type, textColor, socialButtonBackground]);

    useEffect(() => {
        setTab(getTab(currentColor));
    }, [props.open]);

    return (
        <Dialog title={'Select Colour'} {...props}>
            <div className="pt-20 pb-40">
                <div className="flex items-center justify-center">
                    <ZenTabs prefixCls={`zen-card-tabs`} className={`mt-18 mb-22`} activeKey={tab} onChange={setTab}>
                        {tabList(type).map((item) => (
                            <ZenTabPane key={item.key} tab={item.tab} />
                        ))}
                    </ZenTabs>
                </div>
                {tab === 'select' && (
                    <div className={`flex px-30 overflow-auto pt-16`}>
                        <div className={`m-auto flex`}>
                            {defaultColors.map((colorList, i) => (
                                <div key={i} className={`flex flex-col mx-4`}>
                                    {colorList.map(
                                        (color, ci) =>
                                            (type === 'background' || ci !== 0) && (
                                                <div
                                                    key={ci}
                                                    onClick={() => {
                                                        handleChangeColor(type, color);
                                                    }}
                                                    className={classNames([
                                                        `p-2 border-3 rounded-circle my-4`,
                                                        {
                                                            'border-success': color === currentColor?.color,
                                                            'border-white': color !== currentColor?.color,
                                                        },
                                                    ])}
                                                >
                                                    <div
                                                        style={{ background: color }}
                                                        className={`w-32 h-32 rounded-circle border border-primary-40`}
                                                    />
                                                </div>
                                            ),
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {tab === 'custom' && (
                    <div className={`px-30 pt-16`}>
                        <ColorPicker
                            color={currentColor?.color}
                            onChange={({ hex }) => {
                                handleChangeColor(type, hex);
                            }}
                        />
                    </div>
                )}
                {tab === 'image' && (
                    <div className={'px-30 pt-16'}>
                        <UploadImageInput
                            className="h-280"
                            value={currentColor?.backgroundImage}
                            onChange={handleChangeBackground}
                            imageClassName={'max-h-full max-w-full'}
                        />
                        <Select
                            className={`mt-12`}
                            fullWidth={true}
                            defaultValue={'fill'}
                            value={currentColor?.backgroundStyle}
                            onChange={(e) => {
                                handleChangeBackgroundStyle(e.target.value);
                            }}
                        >
                            <MenuItem value={'fill'}>Fill</MenuItem>
                            <MenuItem value={'tile'}>Tile</MenuItem>
                        </Select>
                    </div>
                )}
            </div>
        </Dialog>
    );
};

export default ChooseColorModal;
